import { ThemeEvents } from '@theme/events';
import '@theme/dialog';

/**
 * Timber / Heater comparison popup for the Variant Picker.
 *
 * - ONE shared <dialog-component> (the theme's own DialogComponent) lives on <body>, so the
 *   variant picker morphing its DOM can never destroy or duplicate it.
 * - The table content is server-rendered by snippets/nova-variant-compare.liquid inside the picker
 *   (hidden `[data-nova-compare-source]`). It is copied into the dialog every time it opens, so
 *   the popup can never show stale data, and it is refreshed on `variant:update` while open.
 * - Everything is event delegation on `document`, so the links keep working after every picker
 *   re-render, and the module guards against being initialised twice.
 * - Display only: no cart, price or variant requests.
 */

const DIALOG_ID = 'nova-compare-dialog';
const TRIGGER_SELECTOR = '[data-nova-compare-trigger]';
const CLOSE_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19"/></svg>';

/** @type {{ kind: string, blockId: string, productId: string, trigger: HTMLElement | null } | null} */
let active = null;
let opening = false;

/** @returns {import('@theme/dialog').DialogComponent | null} */
function getDialogComponent() {
  return /** @type {any} */ (document.getElementById(DIALOG_ID));
}

/**
 * Creates the shared dialog once.
 * @returns {import('@theme/dialog').DialogComponent}
 */
function ensureDialog() {
  const existing = getDialogComponent();
  if (existing) return existing;

  const component = document.createElement('dialog-component');
  component.id = DIALOG_ID;
  component.className = 'nova-compare';
  component.innerHTML = `
    <dialog ref="dialog" class="nova-compare__dialog" aria-labelledby="${DIALOG_ID}-title">
      <div class="nova-compare__header">
        <h2 class="nova-compare__title" id="${DIALOG_ID}-title" ref="title"></h2>
        <button type="button" class="nova-compare__close" ref="closeButton" aria-label="Close comparison">${CLOSE_ICON}</button>
      </div>
      <div class="nova-compare__body" ref="body" tabindex="-1"></div>
    </dialog>`;

  document.body.appendChild(component);

  const { closeButton, dialog } = /** @type {any} */ (component).refs;

  closeButton.addEventListener('click', () => /** @type {any} */ (component).closeDialog());

  // Native cancel (e.g. Escape while focus is on the dialog itself) goes through the same animated close.
  dialog.addEventListener('cancel', (/** @type {Event} */ event) => {
    event.preventDefault();
    /** @type {any} */ (component).closeDialog();
  });

  component.addEventListener('dialog:close', onClosed);

  // The table can only be measured once the dialog is visible.
  component.addEventListener('dialog:open', () => updateScrollHint(/** @type {any} */ (component).refs.body));

  return /** @type {any} */ (component);
}

/**
 * Finds the server-rendered source for a picker.
 * @param {string} kind
 * @param {string} blockId
 * @param {string} productId
 * @returns {Element | null}
 */
function findSource(kind, blockId, productId) {
  const pickers = Array.from(document.querySelectorAll('variant-picker'));
  const picker =
    pickers.find(
      (el) => /** @type {HTMLElement} */ (el).dataset.blockId === blockId && /** @type {HTMLElement} */ (el).dataset.productId === productId
    ) || null;

  return picker ? picker.querySelector(`[data-nova-compare-source="${kind}"]`) : null;
}

/**
 * Copies a source into the dialog.
 * @param {any} component
 * @param {Element} source
 */
function fillDialog(component, source) {
  const { title, body, dialog } = component.refs;

  title.textContent = /** @type {HTMLElement} */ (source).dataset.title || '';
  component.className = `nova-compare nova-compare--${active?.kind ?? ''}`;

  const scrollTop = body.scrollTop;
  body.replaceChildren();

  const hint = document.createElement('p');
  hint.className = 'nova-compare__hint';
  hint.textContent = 'Scroll sideways to compare all options →';
  body.appendChild(hint);

  for (const child of Array.from(source.children)) {
    body.appendChild(child.cloneNode(true));
  }

  body.scrollTop = scrollTop;
  dialog.dataset.kind = active?.kind ?? '';

  updateScrollHint(body);
}

/**
 * Shows the "swipe" cue only when the table really overflows.
 * @param {HTMLElement} body
 */
function updateScrollHint(body) {
  requestAnimationFrame(() => {
    const scroller = body.querySelector('[data-nova-compare-scroll]');
    const hint = body.querySelector('.nova-compare__hint');
    if (!scroller || !hint) return;

    const overflows = scroller.scrollWidth > scroller.clientWidth + 1;
    hint.setAttribute('data-visible', String(overflows));
  });
}

/**
 * @param {Event} event
 */
function onTriggerClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  const trigger = target ? /** @type {HTMLElement | null} */ (target.closest(TRIGGER_SELECTOR)) : null;
  if (!trigger) return;

  event.preventDefault();
  if (opening) return;

  const kind = trigger.dataset.novaCompareTrigger || '';
  const picker = /** @type {HTMLElement | null} */ (trigger.closest('variant-picker'));
  if (!kind || !picker) return;

  const blockId = picker.dataset.blockId || '';
  const productId = picker.dataset.productId || '';
  const source = findSource(kind, blockId, productId);
  if (!source) return;

  const component = ensureDialog();
  if (component.refs.dialog.open) return;

  opening = true;
  active = { kind, blockId, productId, trigger };
  fillDialog(component, source);

  component.addEventListener('dialog:open', () => (opening = false), { once: true });
  component.showDialog();

  // Safety net if the dialog could not open for any reason.
  window.setTimeout(() => (opening = false), 600);
}

/**
 * Restores focus to the link that opened the popup (native <dialog> already does this when the
 * trigger element survived; this covers a trigger that was re-created by the picker meanwhile).
 */
function onClosed() {
  const previous = active;
  active = null;
  opening = false;
  if (!previous) return;

  const stillThere = previous.trigger && document.contains(previous.trigger) ? previous.trigger : null;
  const replacement = Array.from(document.querySelectorAll(`${TRIGGER_SELECTOR}[data-nova-compare-trigger="${previous.kind}"]`)).find(
    (el) => {
      const picker = /** @type {HTMLElement | null} */ (el.closest('variant-picker'));
      return picker?.dataset.blockId === previous.blockId && picker?.dataset.productId === previous.productId;
    }
  );

  const focusTarget = /** @type {HTMLElement | null} */ (stillThere || replacement || null);
  if (focusTarget && document.activeElement !== focusTarget) {
    focusTarget.focus({ preventScroll: true });
  }
}

/** Keeps an open popup in sync if the picker re-renders while it is open. */
function onVariantUpdate() {
  if (!active) return;

  const component = getDialogComponent();
  if (!component || !component.refs.dialog.open) return;

  requestAnimationFrame(() => {
    if (!active) return;
    const source = findSource(active.kind, active.blockId, active.productId);
    if (source) fillDialog(component, source);
  });
}

/** Re-measures the scroll cue when the viewport changes (rotation, resize) while the popup is open. */
function onResize() {
  const component = getDialogComponent();
  if (component?.refs.dialog.open) updateScrollHint(component.refs.body);
}

/** Closes the popup if the picker section is removed (theme editor). */
function onSectionUnload() {
  const component = getDialogComponent();
  if (component?.refs.dialog.open) component.closeDialog();
}

const FLAG = '__novaVariantCompareReady';

if (!(/** @type {any} */ (window)[FLAG])) {
  /** @type {any} */ (window)[FLAG] = true;

  document.addEventListener('click', onTriggerClick);
  document.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  window.addEventListener('resize', onResize);
}
